import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Maximize2, Minimize2, RefreshCw, Loader2, ArrowLeft, Radio } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';  // same-origin by default
const POLL_INTERVAL_MS = 5000;
const NEXT_LIMIT = 5;

/**
 * Format athlete name for Telão display.
 * Capitalizes first letter of each word (title case).
 * If the 2nd word is a particle (de/da/do/dos/das), includes it + the next name.
 * Otherwise takes just first name + surname.
 */
function formatTelaoName(fullName) {
  if (!fullName) return '';
  const particles = ['de', 'da', 'do', 'dos', 'das'];
  const raw = fullName.trim().split(/\s+/);
  if (raw.length === 0) return '';
  const cap = (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  const first = cap(raw[0]);
  if (raw.length === 1) return first;
  const secondLower = raw[1].toLowerCase();
  if (particles.includes(secondLower)) {
    if (raw.length >= 3) return first + ' ' + cap(raw[1]) + ' ' + cap(raw[2]);
    return first + ' ' + cap(raw[1]);
  }
  return first + ' ' + cap(raw[1]);
}

/**
 * TelaoPage — Live broadcast screen ("Telão") for an event venue.
 * Public, fullscreen, dark + gold theme, polls every 5s.
 */
export default function TelaoPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const containerRef = useRef(null);

  const fetchTelao = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/championships/${id}/telao`, {
        params: { limit: NEXT_LIMIT },
      });
      setData(res.data);
      setError(null);
      setPulse(true);
      setTimeout(() => setPulse(false), 600);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Não foi possível carregar o Telão');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTelao();
    const t = setInterval(fetchTelao, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchTelao]);

  // Listen to fullscreen changes (Esc key, etc.)
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch {
      /* ignore */
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4 text-white p-8">
        <p className="text-xl text-red-400" data-testid="telao-error">{error}</p>
        <Link to="/public" className="text-[#D4AF37] hover:underline">Voltar ao público</Link>
      </div>
    );
  }

  const champ = data?.championship || {};
  const nextMatches = data?.next_matches || [];
  const upcoming = nextMatches[0];
  const rest = nextMatches.slice(1);
  const updatedAt = data?.updated_at ? new Date(data.updated_at) : null;

  return (
    <div
      ref={containerRef}
      data-testid="telao-page"
      className="min-h-screen bg-black text-white relative overflow-hidden"
      style={{
        backgroundImage:
          'radial-gradient(circle at 10% 0%, rgba(212,175,55,0.08) 0%, transparent 40%), radial-gradient(circle at 90% 100%, rgba(212,175,55,0.06) 0%, transparent 50%)',
      }}
    >
      {/* Top bar */}
      <header className="flex items-center justify-between px-8 py-5 border-b border-[#D4AF37]/20">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-lg flex items-center justify-center">
            <Trophy className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]/70 font-semibold">
              Telão · Transmissão ao Vivo
            </p>
            <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight" data-testid="telao-championship-name">
              {champ.name || 'Campeonato'}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <Radio className={`w-4 h-4 text-[#D4AF37] ${pulse ? 'animate-pulse' : ''}`} />
            <span className="text-xs text-[#D4AF37]/90 font-mono">
              AO VIVO {updatedAt ? `· ${updatedAt.toLocaleTimeString('pt-BR')}` : ''}
            </span>
          </div>
          <button
            onClick={fetchTelao}
            data-testid="telao-refresh-btn"
            title="Atualizar agora"
            className="w-10 h-10 rounded-lg border border-[#27272A] hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5 transition-all flex items-center justify-center"
          >
            <RefreshCw className="w-4 h-4 text-[#A1A1AA]" />
          </button>
          <button
            onClick={toggleFullscreen}
            data-testid="telao-fullscreen-btn"
            title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
            className="w-10 h-10 rounded-lg border border-[#27272A] hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5 transition-all flex items-center justify-center"
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4 text-[#A1A1AA]" />
            ) : (
              <Maximize2 className="w-4 h-4 text-[#A1A1AA]" />
            )}
          </button>
          {!isFullscreen && (
            <Link
              to="/public"
              data-testid="telao-back-btn"
              className="w-10 h-10 rounded-lg border border-[#27272A] hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5 transition-all flex items-center justify-center"
              title="Voltar"
            >
              <ArrowLeft className="w-4 h-4 text-[#A1A1AA]" />
            </Link>
          )}
        </div>
      </header>

      {/* Main */}
      <main className="px-8 py-8">
        {nextMatches.length === 0 ? (
          <div className="text-center py-32 space-y-4">
            <Trophy className="w-20 h-20 text-[#D4AF37]/40 mx-auto" />
            <p className="text-3xl text-[#A1A1AA] font-semibold" data-testid="telao-empty">
              Nenhuma luta na fila no momento
            </p>
            <p className="text-base text-[#71717A]">
              Aguardando a próxima chamada — a tela atualiza automaticamente a cada 5 segundos.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Próxima luta — destaque grande */}
            <section className="lg:col-span-2" data-testid="telao-next-match">
              <p className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] mb-4 font-bold">
                ► Próxima Luta
              </p>
              <div className="rounded-2xl border-2 border-[#D4AF37] bg-gradient-to-br from-[#0a0a0a] to-[#1a1410] p-8 lg:p-12 shadow-[0_0_60px_rgba(212,175,55,0.15)]">
                <div className="flex flex-wrap gap-2 mb-6">
                  <span className="text-micro uppercase tracking-widest font-bold bg-[#D4AF37] text-black px-3 py-1 rounded">
                    {upcoming.round_label}
                  </span>
                  <span className="text-micro uppercase tracking-widest font-bold border border-[#D4AF37]/40 text-[#D4AF37] px-3 py-1 rounded">
                    Chave #{upcoming.bracket_number} · Cód. {upcoming.bracket_code}
                  </span>
                  {upcoming.is_absolute && (
                    <span className="text-micro uppercase tracking-widest font-bold bg-purple-500/20 text-purple-300 border border-purple-500/40 px-3 py-1 rounded">
                      Absoluto
                    </span>
                  )}
                </div>

                <p className="text-base lg:text-lg text-[#D4AF37]/80 uppercase tracking-wider mb-8 font-semibold">
                  {upcoming.category_name}
                </p>

                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 lg:gap-8">
                  {/* Athlete 1 */}
                  <div className="text-right">
                    <p className="text-3xl lg:text-5xl xl:text-6xl font-black text-white leading-tight break-words" data-testid="telao-athlete1-name">
                      {formatTelaoName(upcoming.athlete1_name)}
                    </p>
                    {upcoming.athlete1_team && (
                      <p className="text-base lg:text-xl text-[#D4AF37]/70 mt-2 font-medium">
                        {upcoming.athlete1_team}
                      </p>
                    )}
                  </div>

                  {/* VS */}
                  <div className="text-4xl lg:text-6xl font-black text-[#D4AF37] px-2 lg:px-6 select-none">
                    VS
                  </div>

                  {/* Athlete 2 */}
                  <div className="text-left">
                    <p className="text-3xl lg:text-5xl xl:text-6xl font-black text-white leading-tight break-words" data-testid="telao-athlete2-name">
                      {formatTelaoName(upcoming.athlete2_name)}
                    </p>
                    {upcoming.athlete2_team && (
                      <p className="text-base lg:text-xl text-[#D4AF37]/70 mt-2 font-medium">
                        {upcoming.athlete2_team}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Próximas seguintes */}
            <aside data-testid="telao-upcoming-list">
              <p className="text-xs uppercase tracking-[0.4em] text-[#A1A1AA] mb-4 font-bold">
                ► Em Seguida
              </p>
              <div className="space-y-3">
                {rest.length === 0 && (
                  <div className="rounded-lg border border-[#27272A] bg-[#0a0a0a] p-5 text-center text-sm text-[#71717A]">
                    Esta é a única luta na fila.
                  </div>
                )}
                {rest.map((m, idx) => (
                  <div
                    key={m.match_id}
                    data-testid={`telao-upcoming-${idx}`}
                    className="rounded-lg border border-[#27272A] bg-[#0a0a0a]/80 p-4 hover:border-[#D4AF37]/30 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-micro uppercase tracking-widest text-[#D4AF37] font-bold">
                        #{idx + 2}
                      </span>
                      <span className="text-micro uppercase tracking-widest bg-[#1a1a1a] text-[#A1A1AA] px-2 py-0.5 rounded">
                        {m.round_label}
                      </span>
                      <span className="text-micro uppercase tracking-widest text-[#71717A]">
                        Chave #{m.bracket_number}
                      </span>
                    </div>
                    <p className="text-xs text-[#D4AF37]/70 uppercase tracking-wider mb-2 line-clamp-1">
                      {m.category_name}
                    </p>
                    <div className="space-y-1">
                      <p className="text-base lg:text-lg font-bold text-white leading-tight">
                        {formatTelaoName(m.athlete1_name)}
                        {m.athlete1_team && (
                          <span className="text-xs text-[#A1A1AA] font-normal ml-2">/ {m.athlete1_team}</span>
                        )}
                      </p>
                      <p className="text-micro text-[#D4AF37] font-bold">VS</p>
                      <p className="text-base lg:text-lg font-bold text-white leading-tight">
                        {formatTelaoName(m.athlete2_name)}
                        {m.athlete2_team && (
                          <span className="text-xs text-[#A1A1AA] font-normal ml-2">/ {m.athlete2_team}</span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {data?.total_open > nextMatches.length && (
                <p className="mt-4 text-xs text-[#71717A] text-center">
                  +{data.total_open - nextMatches.length} luta(s) aguardando na fila
                </p>
              )}
            </aside>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="absolute bottom-0 left-0 right-0 px-8 py-3 border-t border-[#D4AF37]/10 flex items-center justify-between text-micro text-[#71717A] uppercase tracking-widest">
        <span>E-torneios · Sistema de Gerenciamento BJJ</span>
        <span>Atualização automática a cada 5s</span>
      </footer>
    </div>
  );
}
